import React, { useState, useContext, useEffect, useMemo, useRef } from 'react';
import DocViewer, { DocViewerRenderers } from "react-doc-viewer";
import FileViewer from 'react-file-viewer';
import ReactJWPlayer from 'react-jw-player';
import { makeStyles } from '@material-ui/core'
import AppDataContext from '../../../common/AppContext';
import { CurriculumMasterData } from '../../../services'
import SectionLoader from "../../../sharedComponents/Loader/SectionLoader";
import { cacheStorage } from './cacheStorage';
import AppConstants from '../../../common/AppConstants';
import clsx from 'clsx';

const useStyles = makeStyles(theme => ({
    root: {
        display: 'flex',
        // flexGrow: 1,
        justifyContent: 'center',
        '& .jw-knob': {
            visibility: 'hidden'
        },
        '& .pg-viewer-wrapper': {
            height: '350px',
            overflowY: 'auto'
        },
        overflowY: 'auto',
        minHeight: ({ minHeight }) => minHeight ?? '500px',
        background: 'transparent'
    },
    videoPlayer: {
        '& .jwplayer': {
            width: '100%',
            [theme.breakpoints.down('md')]: {
                height: '320px',
            },
            [theme.breakpoints.up('xl')]: {
                width: `${theme.breakpoints.values.xl - 100}px !important`
            },
            [theme.breakpoints.up('lg')]: {
                width: `${theme.breakpoints.values.lg - 100}px !important`
            },
            [theme.breakpoints.up('md')]: {
                width: `${theme.breakpoints.values.md - 100}px !important`
            },
        },
    },
    docView: {

        height: ({ minHeight }) => minHeight ?? '500px',
        '& #image-renderer':{
            justifyContent: 'flex-start',
        },
        '& .pg-viewer-wrapper': {
            overflowY: 'auto'
        },


    },
    fileView: {
        '& .photo-viewer-container': {
            width: 'auto !important'
        },
        '& .pg-viewer-wrapper': {
            overflowY: 'auto'
        },
        '& > img':{
            [theme.breakpoints.up('xl')]: {
                width: `${theme.breakpoints.values.xl - 100}px !important`
            },
            [theme.breakpoints.up('lg')]: {
                width: `${theme.breakpoints.values.lg - 100}px !important`
            },
            [theme.breakpoints.up('md')]: {
                width: `${theme.breakpoints.values.md - 100}px !important`
            },
        }
    }
}))

const DocumentViewer = ({ fileExtension, documentName, handleComplete, minHeight, isFromQuiz = false }) => {
    const { jwtTokenDetails } = useContext(AppDataContext)
    const playerRef = useRef(null);
    const [docExtension, setDocExtension] = useState(null);
    const [fileType, setFileType] = useState(null);
    const [fileName, setFileName] = useState();
    // const [numPages, setNumPages] = useState(null);
    // const [pageNumber, setPageNumber] = useState(1);
    // const [data, setData] = useState(props.data);
    //const [pdfData, setPdfData] = useState('');

    const extensionInfo = [{ ext: 'aac', fileType: 'audio/aac' },
    { ext: 'abw', fileType: 'application/x-abiword' },
    { ext: 'arc', fileType: 'application/x-freearc' },
    { ext: 'avi', fileType: 'video/x-msvideo' },
    { ext: 'azw', fileType: 'application/vnd.amazon.ebook' },
    { ext: 'bin', fileType: 'application/octet-stream' },
    { ext: 'bmp', fileType: 'image/bmp' },
    { ext: 'bz', fileType: 'application/x-bzip' },
    { ext: 'bz2', fileType: 'application/x-bzip2' },
    { ext: 'cda', fileType: 'application/x-cdf' },
    { ext: 'csh', fileType: 'application/x-csh' },
    { ext: 'css', fileType: 'text/css' },
    { ext: 'csv', fileType: 'text/csv' },
    { ext: 'doc', fileType: 'application/msword' },
    { ext: 'docx', fileType: 'application/vnd.openxmlformats-officedocument.wordprocessingml.document' },
    { ext: 'eot', fileType: 'application/vnd.ms-fontobject' },
    { ext: 'epub', fileType: 'application/epub+zip' },
    { ext: 'gz', fileType: 'application/gzip' },
    { ext: 'gif', fileType: 'image/gif' },
    { ext: 'htm', fileType: 'text/html' },
    { ext: 'html', fileType: 'text/html' },
    { ext: 'ico', fileType: 'image/vnd.microsoft.icon' },
    { ext: 'ics', fileType: 'text/calendar' },
    { ext: 'jar', fileType: 'application/java-archive' },
    { ext: 'jpeg', fileType: 'image/jpeg' },
    { ext: 'jpg', fileType: 'image/jpeg' },
    { ext: 'jfif', fileType: 'image/jpeg' },
    { ext: 'js', fileType: 'text/javascript' },
    { ext: 'json', fileType: 'application/json' },
    { ext: 'jsonld', fileType: 'application/ld+json' },
    { ext: 'mid', fileType: 'audio/midi' },
    { ext: 'midi', fileType: ' audio/x-midi' },
    { ext: 'mjs', fileType: 'text/javascript' },
    { ext: 'mp3', fileType: 'audio/mpeg' },
    { ext: 'mp4', fileType: 'video/mp4' },
    { ext: 'mov', fileType: 'video/quicktime' },
    { ext: 'mpeg', fileType: 'video/mpeg' },
    { ext: 'mpkg', fileType: 'application/vnd.apple.installer+xml' },
    { ext: 'odp', fileType: 'application/vnd.oasis.opendocument.presentation' },
    { ext: 'ods', fileType: 'application/vnd.oasis.opendocument.spreadsheet' },
    { ext: 'odt', fileType: 'application/vnd.oasis.opendocument.text' },
    { ext: 'oga', fileType: 'audio/ogg' },
    { ext: 'ogv', fileType: 'video/ogg' },
    { ext: 'ogx', fileType: 'application/ogg' },
    { ext: 'opus', fileType: 'audio/opus' },
    { ext: 'otf', fileType: 'font/otf' },
    { ext: 'png', fileType: 'image/png' },
    { ext: 'pdf', fileType: 'application/pdf' },
    { ext: 'php', fileType: 'application/x-httpd-php' },
    { ext: 'ppt', fileType: 'application/vnd.ms-powerpoint' },
    { ext: 'pptx', fileType: 'application/vnd.openxmlformats-officedocument.presentationml.presentation' },
    { ext: 'rar', fileType: 'application/vnd.rar' },
    { ext: 'rtf', fileType: 'application/rtf' },
    { ext: 'sh', fileType: 'application/x-sh' },
    { ext: 'svg', fileType: 'image/svg+xml' },
    { ext: 'swf', fileType: 'application/x-shockwave-flash' },
    { ext: 'tar', fileType: 'application/x-tar' },
    { ext: 'tif', fileType: 'image/tiff' },
    { ext: 'tiff', fileType: 'image/tiff' },
    { ext: 'ts', fileType: 'video/mp2t' },
    { ext: 'ttf', fileType: 'font/ttf' },
    { ext: 'txt', fileType: 'text/plain' },
    { ext: 'vsd', fileType: 'application/vnd.visio' },
    { ext: 'wav', fileType: 'audio/wav' },
    { ext: 'weba', fileType: 'audio/webm' },
    { ext: 'webm', fileType: 'video/webm' },
    { ext: 'webp', fileType: 'image/webp' },
    { ext: 'woff', fileType: 'font/woff' },
    { ext: 'woff2', fileType: 'font/woff2' },
    { ext: 'xhtml', fileType: 'application/xhtml+xml' },
    { ext: 'xls', fileType: 'application/vnd.ms-excel' },
    { ext: 'xlsx', fileType: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' },
    { ext: 'xml', fileType: 'text/xml' },
    { ext: 'xul', fileType: 'application/vnd.mozilla.xul+xml' },
    { ext: 'zip', fileType: 'application/zip' },
    { ext: '3gp', fileType: 'video/3gpp' },
    { ext: '3g2', fileType: 'video/3gpp2' },
    { ext: '7z', fileType: 'application/x-7z-compressed' }];

    const classes = useStyles({ minHeight });

    const [videoWatchComplete, setVideoWatchComplete] = useState(false)
    const [position, setPosition] = useState(0);
    const [mediaUrl, setMediaUrl] = useState();
    const [isLoading, setIsLoading] = useState(true);

    useEffect(() => {
        setMediaUrl('')
        setFileType(null)
        setIsLoading(true)
        setFileName(documentName)
        setDocExtension(fileExtension)
    }, [documentName, fileExtension])

    useEffect(() => {
        if (videoWatchComplete) {
            handleComplete && handleComplete(true)
        }
    }, [videoWatchComplete])

    useEffect(() => {
        if (isLoading == true) {
            setVideoWatchComplete(false);

            if (fileName && docExtension) {
                let mediaUrl = cacheStorage.getPhotoFromLocalStorage(fileName);
                if (mediaUrl) {
                    setIsLoading(false);
                    setMediaUrl(mediaUrl);
                }
                else {
                    if (docExtension == 'mov' || docExtension == 'mp4' || docExtension == 'video') {
                        promiseWithTimeout(getMediaURL(fileName, docExtension), AppConstants.REQUEST_TIMEOUT_TIME)
                            .then(res => {
                                console.log(res);
                            }).catch(err => {
                                setVideoWatchComplete(true)
                            })
                    } else {
                        getMediaURL(fileName, docExtension)
                    }
                }
                let fileTypeIndex = extensionInfo.findIndex(x => x.ext === docExtension);
                if (fileTypeIndex && fileTypeIndex >= 0) {
                    let _fileTypeInfo = extensionInfo[fileTypeIndex];
                    let _fileTypeName = _fileTypeInfo.fileType;
                    setFileType(_fileTypeName);
                }
            }
        }
    }, [fileName, docExtension])

    const promiseWithTimeout = (promise, timeout) => {
        const timeoutPromise = new Promise((_, reject) =>
            setTimeout(() => reject(new Error("Timeout exceeded")), timeout)
        );

        return Promise.race([promise, timeoutPromise]);
    }

    const getMediaURL = async (docName) => {

        setIsLoading(true);
        try {
            const result = await CurriculumMasterData.getMediaURL(docName);
            if (result) {
                setMediaUrl(result);
                cacheStorage.savePhotoToLocalStorage(docName, result);
            }
            setIsLoading(false);
        } catch (err) {
            console.log(err);
            setIsLoading(false);
        }

    }


    // useEffect(() => {
    //     console.log("fileExt", fileExtension);

    //     if (documentName && fileExtension) {
    //         let fileTypeIndex = extensionInfo.findIndex(x => x.ext === fileExtension);
    //         if (fileTypeIndex && fileTypeIndex >= 0) {
    //             let _fileTypeInfo = extensionInfo[fileTypeIndex];
    //             let _fileTypeName = _fileTypeInfo.fileType;
    //             setFileType(_fileTypeName);
    //             // setDocName(documentName);
    //             setDocExtension(fileExtension);
    //             let _docInfo = [{ uri: documentName, fileType: _fileTypeName }];
    //             setDocInfo(_docInfo);

    //         }
    //     }
    // }, [documentName, fileExtension])

    const handleOnTime = (e) => {
        //console.log(e);
        const player = window.jwplayer('jwPlayerReferee');
        var positionDiff = e.position - position;
        if (positionDiff > 1) {
            player.seek(position);
        } else {
            setPosition(e.position);
        }
    }



    const videoCompleted = () => {
        setVideoWatchComplete(true)
    }
    return (
        !isLoading ? <>
            {
                (fileExtension == 'mp4' || fileExtension == 'mov') ?
                    <div className={clsx({ [classes.root]: true, [classes.videoPlayer]:!isFromQuiz})} data-mediaid="jwPlayerReferee">
                        <ReactJWPlayer
                            aspectratio='1:1'
                            onContextMenu={(e) => e.preventDefault()}
                            ref={playerRef}
                            playerId='jwPlayerReferee'
                            playerScript={jwtTokenDetails?.jwPlayerSource}
                            licenseKey={jwtTokenDetails?.jwPlayerKey}
                            file={mediaUrl ?? ''}

                            onTime={handleOnTime}
                            onOneHundredPercent={videoCompleted}
                        />
                    </div>
                    :
                    ((fileExtension !== 'pdf' && fileExtension !== 'svg' && fileExtension !== 'png') ?
                        <DocViewer
                            pluginRenderers={DocViewerRenderers}
                            documents={[{ uri: mediaUrl, fileType: fileType }]}
                            config={{
                                header: {
                                    disableHeader: true,
                                    disableFileName: true
                                }
                            }}
                            className={classes.docView}
                        /> :
                        (fileExtension === 'svg' ?
                            <img src={mediaUrl} className={classes.root} style={{ height: minHeight }}></img> :
                            <>
                                {mediaUrl && mediaUrl != '' && <div className={classes.fileView} style={{ height: minHeight }}>
                                    <FileViewer
                                        fileType={fileExtension}
                                        filePath={mediaUrl}
                                    />


                                </div>}
                            </>)
                    )
            }
        </>
            : <SectionLoader />
    )
}

export default DocumentViewer